import { useState, useEffect, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import { parsePhoneNumber as libParsePhone, isValidPhoneNumber } from 'libphonenumber-js/mobile';
import {
  useTranslation,
  Input,
  Checkbox,
  Button,
  ButtonStyles,
  ButtonSize,
  ButtonTypes,
  DropDown,
  useToast,
  Notification,
  Theme,
  InputValidations,
  useSession,
  sendUtagView,
  useTealiumDataLayer,
} from '@nextgen-web-framework/all';
import { Logger } from '@nextgen-web-framework/core';
import {
  ContactType,
  OwnerType,
  RegionalAddressSearchTypeEnum,
  ValidationType,
  profileApi,
  ProfileEventsEnum,
  type CreateContact,
  type AddressContactType,
  type DynamicObjectInterface,
  type Contact,
  type StateData,
  type GuidedData,
} from '@commerce-profile-webcomponents/sdk';
import { useFormik } from 'formik';
import ButtonWithLoading from '../Common/ButtonWithLoading/ButtonWithLoading';
import PhoneNumberInput from '../Common/PhoneNumberInput/PhoneNumberInput';
import { DisplayField } from '../Common/DisplayField/DisplayField';
import { AddressInitialValue } from '../../utils/Utils';
import { MyAccountInput, MyAccountOption } from '../../utils/types/propTypes';
import { MyAccountFieldType, MyAccountExtEnum, ToastMessageEnum } from '../../utils/types/enum';
import { useLocale } from '../../utils/customHooks';
import { useCountryMasterData, useMyAccountAddressConfig } from '../../utils/masterdata';
import {
  getExt,
  isFieldTouched,
  profileValidationSchema,
  conditionalError,
  getCleanString,
} from '../../utils/myAccountProfileUtils';
import {
  getAddressConfig,
  addressFieldLabel,
  addressFieldTypeByInput,
  filterFieldNames,
  submitShippingAddress,
  updateCacheData,
  processDependencyFields,
  formatAddressString,
  handlePhoneChange,
  startSearchDrawer,
} from '../../utils/myAccountAddressUtils';
import { ADDRESS_FIELDS } from '../../utils/Constants';
import InputErrorComponent from '../Common/InputErrorComponent/InputErrorComponent';
import { inputErrorMessage, inputErrorStatus } from '../Common/InputErrorComponent/utils';
import SearchDrawer from '../Common/SearchDrawer/SearchDrawer';
import AddressLookup from '../AddressLookup/AddressLookup';
import { sanitizeInput } from '../../utils/registrationUtils';
import { AddressLookupCallbackProps } from '../AddressLookup/types/propTypes';
import {
  parseString,
  parseNumber,
  parsePhoneNumber,
  ifThenElse,
  parseArray,
  processLabelText,
  iff,
} from '../../utils/helper';
import { useGlobalErrorHandler } from '../../utils/globalErrorHook';
import logObject from '../../../logObject';
import {
  AddressInputProps,
  GuidedCacheDataType,
  AddressExtType,
  AddressPairFieldType,
  AddressInputHandlerType,
} from './types/propTypes';
import styles from './AddressInput.module.scss';
const AddressInput = forwardRef<AddressInputHandlerType, AddressInputProps>(
  (
    {
      contactType = ContactType.Shipping,
      accountNumber,
      ownerId = '', // for shipping address only, this is profileId
      address,
      ownerType = OwnerType.Profile, // Default for shipping address
      className,
      callback,
      validationCB,
      externalUse,
      allowEnterManually = true,
      // isMandatory = false,
      useMobileView = true,
      header,
      useDesktopView,
      disabled,
      disableDeliveryInstructions = false,
      showShippingParticulars = true,
      // From existing shipping form
      title,
      isEdit,
      showDefaultAddressCheckbox,
      hideConfirmButton,
      isLoading = false,
      lookupEnabled,
      // For validation purpose
      validateOnMount,
      updateCallbackOnMount = true,
      message,
      status,
      updateDisableButton,
      useAvailableWidth,
      // For downline bad address handling
      enableErrorFieldsOnly,
    }: AddressInputProps,
    ref,
  ) => {
    const logger = Logger.getInstance(logObject('AddressInput.tsx', 'AddressInput'));
    const dataLayer = useTealiumDataLayer();
    const t = useTranslation();
    const { addToast } = useToast();
    const { session } = useSession();
    const { aboNum } = session;
    const addressTypeLabel = title ?? t(`profile.AddressInput.${contactType.toLowerCase()}Address`);
    const successMessage = t('profile.AddressInput.save.success');
    const failedMessage = t('profile.AddressInput.save.failed');
    const apiErrorHandler = useGlobalErrorHandler();
    const scrollRef = useRef<HTMLDivElement>(null);

    // handle for language
    const currentLocale = useLocale();
    const [shortLocaleCode] = String(currentLocale).split('-');

    const { currentCountry } = useCountryMasterData();
    const defaultDialCode = `(+${currentCountry?.phoneCode})`;
    const countryCode = currentCountry?.pk?.toUpperCase();

    // To customize this function
    const initialValues = AddressInitialValue(contactType as ContactType, accountNumber ?? aboNum, ownerType, ownerId);

    const [showAddressForm, setShowAddressForm] = useState<boolean>(false);
    const [addressLoaded, setAddressLoaded] = useState<boolean>(false);
    const [isAddressChanged, setIsAddressChanged] = useState<boolean>(false);
    const [selectedAddress, setSelectedAddress] = useState<AddressContactType>(initialValues);
    const [manualAddress, setManualAddress] = useState<boolean>(false);

    // Shipping Address
    const [dialCode, setDialCode] = useState('');
    const [phoneNum, setPhoneNum] = useState('');
    const [focusedField, setFocusedField] = useState<string>('');
    const [canSubmit, setCanSubmit] = useState(false);

    // Search drawer
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [isGuidedDataLoading, setIsGuidedDataLoading] = useState(false);
    const [searchDrawerTitle, setSearchDrawerTitle] = useState('');
    const [searchField, setSearchField] = useState<DynamicObjectInterface>({
      fieldName: '',
      dispLabel: '',
      data: [],
      selectedItem: '',
    });

    // Province/Region Data
    const [stateData, setStateData] = useState<StateData[]>([]);
    const [regionData, setRegionData] = useState<GuidedData[]>([]);

    // to store data for guided lookup as cache
    const [lookupCacheData, setLookupCacheData] = useState<GuidedCacheDataType[]>([]);

    // to handle paired fields, first field is primary field, second field is mirroring field
    const [fieldPairs, setFieldPairs] = useState<AddressPairFieldType[]>();

    const [isDefaultAddress, setIsDefaultAddress] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState(false);

    const [enabledCount, setEnabledCount] = useState(0);
    const [readonlyCheck, setReadonlyCheck] = useState(false);
    const [readonlyFields, setReadonlyFields] = useState<DynamicObjectInterface>({});

    // useRef for hidden button
    const refHiddenButton = useRef<HTMLButtonElement | null>(null);

    const addrConfigMaster = useMyAccountAddressConfig();
    const { inputs, ext } = getAddressConfig(addrConfigMaster, contactType as ContactType);
    const isLookupEnabled = lookupEnabled ?? (getExt(MyAccountExtEnum.USE_ADDRESS_LOOKUP, ext) as boolean);
    const isGuidedInput = getExt(MyAccountExtEnum.GUIDED_INPUT, ext) as boolean;
    const guidedInputSkipOptionalField = getExt(MyAccountExtEnum.GUIDED_INPUT_SKIP_OPTIONAL_FIELD, ext) as boolean;
    const showDefaultAddress = getExt(MyAccountExtEnum.SHOW_DEFAULT_ADDRESS, ext) as boolean;

    const showDefaultCheckbox = (showDefaultAddress && !externalUse) || showDefaultAddressCheckbox;

    const isGuidedDataReady = isGuidedInput && regionData?.length > 0;
    // props will override setting from masterdata
    const validationOnMount = validateOnMount ?? !!address?.contactId;

    // We need to move this to masterdata later
    const charCountFields = [
      ADDRESS_FIELDS.DELIVERY_INSTRUCTIONS,
      ADDRESS_FIELDS.ADDRESS_LINE_ONE,
      ADDRESS_FIELDS.ADDRESS_LINE_TWO,
      ADDRESS_FIELDS.ADDRESS_LINE_THREE,
      ADDRESS_FIELDS.CONTACT_NICKNAME,
      ADDRESS_FIELDS.RECIPIENT_FIRST_NAME,
      ADDRESS_FIELDS.RECIPIENT_LAST_NAME,
      // LATAM
      ADDRESS_FIELDS.ADDRESS_TYPE_OTHER,
      ADDRESS_FIELDS.MAIN_ROAD,
      ADDRESS_FIELDS.SIDE_LINE,
      ADDRESS_FIELDS.NUMBER,
    ];

    const validationSchema = profileValidationSchema(inputs);
    const allFields = filterFieldNames(inputs);

    const isMobileView = useMobileView && !useDesktopView; // handle for default useMobileView is true
    const mobileStyle = isMobileView ? styles['mobile'] : '';
    let desktopStyle = ifThenElse(!!useDesktopView, styles['desktop'], '');
    if (useAvailableWidth) {
      desktopStyle = styles['available-width'];
    }

    // Formik setup -->
    const formik = useFormik({
      initialValues: initialValues,
      validationSchema: validationSchema,
      validateOnMount: validationOnMount,
      validateOnBlur: true,
      validateOnChange: true,
      onSubmit: (values: DynamicObjectInterface) => submitShippingForm(values),
    });
    // <-- end of Formik setup

    const customValidate = (disableValidFields = false) => {
      const touched: DynamicObjectInterface = {};

      inputs
        ?.filter((input: MyAccountInput) => input.visible)
        .forEach((input: MyAccountInput) => {
          const { fieldName } = input;
          touched[fieldName] = true;
        });

      formik.setTouched(touched, true);

      // Update state or trigger UI to make fields readonly
      if (disableValidFields) {
        updateReadonlyFields();
      }
    };

    const updateReadonlyFields = async () => {
      const errors = await formik.validateForm();
      const readonlyFields: DynamicObjectInterface = {};
      inputs
        ?.filter((input: MyAccountInput) => input.visible)
        .forEach((input: MyAccountInput) => {
          const { fieldName } = input;
          if (!errors[fieldName]) {
            readonlyFields[fieldName] = true;
          }
        });
      setReadonlyFields(readonlyFields);
    };

    useImperativeHandle(ref, () => ({
      validate: customValidate,
      resetErrors: () => {
        formik.setErrors({});
        formik.setTouched({});
        formik.resetForm({});
      },
      getAddress: getAddress,
    }));

    useEffect(() => {
      if (scrollRef?.current && scrollRef.current?.scrollIntoView) {
        scrollRef.current.scrollIntoView({
          block: 'center',
        });
      }
    }, []);

    useEffect(() => {
      if (isGuidedInput) {
        fetchStateData();
        prepareOptionsData();
      }
    }, [isGuidedInput]);

    useEffect(() => {
      if (stateData) {
        fetchRegionData();
      }
    }, [stateData, currentLocale]);

    useEffect(() => {
      // To control show/hide address form based on param: isLookupEnabled
      setShowAddressForm(!isLookupEnabled);
    }, [isLookupEnabled]);

    useEffect(() => {
      // If set to hide address form, reset the address values
      if (!showAddressForm && addressLoaded) {
        formik.setValues(address ?? initialValues, validationOnMount);
      }
    }, [showAddressForm, addressLoaded]);

    useEffect(() => {
      if (address?.addrLineOne || address?.contactId) {
        // If param - address line 1 is filled, then set it lo state & show address form
        const processAddr = prepareCrossReferenceFields(address as Contact);

        formik.setValues(processAddr, validationOnMount);
        setShowAddressForm(true);

        // Extract phone number
        const phoneNumber = address?.phone && libParsePhone(address.phone);
        if (phoneNumber) {
          setDialCode(`(+${phoneNumber.countryCallingCode})`);
          setPhoneNum(phoneNumber.nationalNumber);
        }
      } else if (isLookupEnabled && !manualAddress) {
        formik.setValues(initialValues);
        setShowAddressForm(false);
      } else {
        // new address
        const processAddr = prepareCrossReferenceFields(address as Contact);

        formik.setValues(processAddr, validationOnMount);
        setShowAddressForm(true);
      }

      // capture original default flag
      setIsDefaultAddress(!!address?.isDefault);

      setTimeout(() => {
        setAddressLoaded(true);
      }, 0);
    }, [address?.addrLineOne, address?.contactId]);

    useEffect(() => {
      // This section is to handle the following issue
      // - LATAM - after enabled the field - need to re-trigger the init process
      // - THA - during submit/otp - to avoid reset the form/data
      if (!disabled && enabledCount === 0 && !enableErrorFieldsOnly) {
        // trigger for first time only
        // when enable the fields
        const processAddr = prepareCrossReferenceFields(address as Contact);
        formik.setValues(processAddr, validationOnMount);
        setEnabledCount((prevCount) => prevCount + 1);
      }
    }, [disabled, address]);

    useEffect(() => {
      // for using address lookup, and address is empty, then reset the address & back to search
      if (formik.values?.addrLineOne === '' && addressLoaded && isLookupEnabled && !manualAddress) {
        formik.setValues(initialValues);
        setShowAddressForm(false);
      }
    }, [formik.values?.addrLineOne, addressLoaded]);

    useEffect(() => {
      // For non-shipping address, update the address to parent component
      const allowedCallback = contactType !== ContactType.Shipping || externalUse;
      if (allowedCallback && Object.keys(formik.touched).length > 0 && callback) {
        // To check is the final address been modified or different from address from lookup
        const finalAddress = getAddress();

        callback?.(finalAddress);
      }
    }, [formik.values, formik.touched, selectedAddress, isAddressChanged, canSubmit]);

    useEffect(() => {
      // For badaddress handling - force to validate and disable valid data fields
      if (enableErrorFieldsOnly && !readonlyCheck && addressLoaded) {
        setTimeout(() => {
          customValidate(true);
        }, 500);

        setReadonlyCheck(true);
      }
    }, [enableErrorFieldsOnly, readonlyCheck, addressLoaded]);

    const getAddress = (values = formik.values) => {
      const cleanFields = removeReferenceFields(values);
      return {
        ...address,
        ...cleanFields,
        // Remove matchId if address not same with selected address or modified
        matchId: ifThenElse(formik.dirty, selectedAddress.matchId, ''),
        addressValidationOverrideFlag: false,
        feValidation: canSubmit,
        validationStatus: ValidationType.Validated,
        contactType: contactType as ContactType,
        countryCode: values?.countryCode ?? countryCode,
        language: shortLocaleCode,
        contactId: parseString(address?.contactId),
      } as AddressContactType;
    };

    useEffect(() => {
      const isPhoneValid = validatePhone(dialCode, phoneNum);
      const enableButton = formik.isValid && isPhoneValid && !!dialCode;
      if (contactType === ContactType.Shipping) {
        const sendSignal = updateCallbackOnMount ? updateCallbackOnMount : Object.keys(formik.touched).length > 0;
        setCanSubmit(enableButton && sendSignal);
      } else {
        updateCanSubmit();
      }
    }, [formik.values, formik.touched, dialCode, phoneNum, formik.isValid, contactType, updateCallbackOnMount]);

    useEffect(() => {
      updateDisableButton?.(canSubmit);
    }, [canSubmit]);

    // Core address search function -->
    const addressLookupCallback = (data: AddressLookupCallbackProps) => {
      // This is callback from AddressLookup component
      if (data.manualEntry) {
        setShowAddressForm(true);
        setSelectedAddress(initialValues);
        setManualAddress(true);
      } else if (data.address?.addrLineOne) {
        const { address } = data;
        formik.setValues({
          ...formik.values,
          addrLineOne: address?.addrLineOne,
          addrLineTwo: address?.addrLineTwo,
          addrLineThree: address?.addrLineThree,
          state: address?.state,
          city: address?.city,
          postalCode: address?.postalCode,
          countryCode: address?.countryCode,
          matchId: address?.matchId,
        });
        formik.setTouched({ addrLineOne: true }); // set at least one field to touched
        setSelectedAddress(address as AddressContactType);
        setIsAddressChanged(true);
        setShowAddressForm(true);
      }
    };
    // <-- end of Core address search function

    // Util -->
    const toastMessage = (theme: Theme, message: string) => {
      addToast({
        id: ToastMessageEnum.SHIPPING_ADDRESS_VALIDATION_MESSAGE,
        timeout: 3000,
        content: <Notification hasNotificationIcon={true} theme={theme as DynamicObjectInterface} message={message} />,
      });
    };
    const validatePhone = (p_dialCode?: string, p_phone?: string) =>
      p_dialCode && p_phone ? isValidPhoneNumber(parsePhoneNumber(p_dialCode, p_phone)) : false;
    const isValidField = (fieldName: string) => inputs.find((input: MyAccountInput) => input.fieldName === fieldName);
    // <-- end of Util

    const removeReferenceFields = (address: DynamicObjectInterface): DynamicObjectInterface => {
      const addr = JSON.parse(JSON.stringify(address));
      // Remove all reference fields
      Object.keys(addr).forEach((key: string) => {
        const pair = fieldPairs?.find((pair: AddressPairFieldType) => pair.secondaryField === key);
        if (pair) {
          // if copyData is true, then copy data from secondary to primary
          const { primaryField, copyData } = pair;
          if (copyData && addr[key as keyof Contact]) {
            addr[primaryField as keyof Contact] = addr[key as keyof Contact];
          }
        }
      });

      // Process post construct field
      const postConstructField = inputs?.find((input: MyAccountInput) =>
        input?.ext?.some((ext: MyAccountOption) => ext.key === MyAccountExtEnum.CONSTRUCT_FROM),
      );

      if (postConstructField) {
        addr[postConstructField.fieldName] = formatAddressString(t, addr, postConstructField, inputs);
      }
      return addr as Contact;
    };

    // Guided input functions -->
    const fetchStateData = async () => {
      setIsGuidedDataLoading(true);
      const data = await apiErrorHandler(() =>
        profileApi.getAddressRegionData(RegionalAddressSearchTypeEnum.STATE, shortLocaleCode?.toLowerCase()),
      );
      setStateData(data);
      setIsGuidedDataLoading(false);
    };
    const prepareOptionsData = () => {
      const fieldWithOptions = inputs.filter(
        (input: MyAccountInput) =>
          input.fieldType === MyAccountFieldType.GUIDED_INPUT && input?.options && input?.options.length > 0,
      );

      const lookupCache = [...lookupCacheData];
      fieldWithOptions?.forEach(({ fieldName, options }: MyAccountInput) => {
        const dropdownData = options?.map((item: DynamicObjectInterface) => ({
          ...item,
          // Key = label key
          // Value = data value
          name: t(`profile.AddressInput.${fieldName}.${item?.key}`), // display
        }));
        lookupCache.push({
          fieldName: fieldName,
          searchKeyword: '',
          data: dropdownData as DynamicObjectInterface[],
        });
      });
      setLookupCacheData(lookupCache);
    };

    const fetchRegionData = () => {
      // based on data from API, loop and get the value and label from stateData
      let guidedData: GuidedData[] = [];
      guidedData = stateData?.map((item: StateData) => {
        const displayName = t(`profile.AddressInput.state.${countryCode}.${item?.state?.replaceAll(' ', '_')}`);
        return {
          value: parseString(item?.state),
          name: parseString(displayName),
        };
      });

      setRegionData(guidedData);
    };

    const guidedInputConfig = async (fieldName: string, lookupSearchKey?: string, options?: MyAccountOption) => {
      // This could be search from UI or filter form backend
      let dropdownData = [];
      if (fieldName === ADDRESS_FIELDS.STATE) {
        dropdownData = regionData;
      } else if (options && Array.isArray(options)) {
        const optionData = getGuidedOptions(fieldName);
        const { data } = optionData || {};
        dropdownData = parseArray(data);
      } else {
        let searchKey = '';
        let dependencyValue = '';
        const { state, district, city, subDistrict } = formik.values;

        let districtValue = district;
        if (!isValidField(ADDRESS_FIELDS.DISTRICT)) {
          // Divert search value to city instead of district
          districtValue = city;
        }

        let subDistrictValue = subDistrict;
        if (!isValidField(ADDRESS_FIELDS.SUB_DISTRICT)) {
          // remove subDistrict value if not using the field
          subDistrictValue = '-';
        }

        const key = lookupSearchKey || fieldName;

        switch (key) {
          case ADDRESS_FIELDS.DISTRICT:
            searchKey = RegionalAddressSearchTypeEnum.DISTRICT;
            dependencyValue = parseString(state);
            break;
          case ADDRESS_FIELDS.SUB_DISTRICT:
            searchKey = RegionalAddressSearchTypeEnum.SUBDISTRICT;
            dependencyValue = `${parseString(state)}/${parseString(districtValue)}`;
            break;
          case ADDRESS_FIELDS.POSTAL_CODE:
            searchKey = RegionalAddressSearchTypeEnum.POSTALCODE;
            dependencyValue = `${parseString(state)}/${parseString(districtValue)}/${parseString(subDistrictValue)}`;
            break;
          default:
        }

        let data: DynamicObjectInterface[];

        // Search in the cache first
        const cachedItem = getGuidedOptions(fieldName, dependencyValue);

        if (cachedItem) {
          const { data: cacheData } = cachedItem;
          data = cacheData;
        } else {
          data = await getAddressSearchData(searchKey, state, districtValue, subDistrictValue);

          // Update to cache
          updateCacheData(fieldName, dependencyValue, data, setLookupCacheData);
        }

        dropdownData = data?.map((text: string) => ({
          value: text,
          name: text,
        }));
      }

      return {
        data: dropdownData,
      };
    };

    const getGuidedOptions = (fieldName: string, searchKey = '') => {
      // This is for guided input only
      const cachedItem = lookupCacheData?.find(
        (item: GuidedCacheDataType) => item.fieldName === fieldName && item.searchKeyword === searchKey,
      );
      return cachedItem;
    };

    const prepareCrossReferenceFields = (addr: Contact) => {
      let processAddr = {
        ...addr,
      };

      const pairs: DynamicObjectInterface[] = [];
      // To update field value if match the scenario - visibleOnValue
      // If dependent value is matched, then current field will show as it

      inputs
        ?.filter((input: MyAccountInput) =>
          input?.ext?.some((ext: MyAccountOption) =>
            [MyAccountExtEnum.VISIBLE_IF, MyAccountExtEnum.DISABLE_FIELD_ON_CHECK].includes(
              ext.key as MyAccountExtEnum,
            ),
          ),
        )
        ?.forEach(({ fieldName, ext }: MyAccountInput) => {
          // Guided Input - Others
          // Check from secondary field to primary field (from other to option)
          const visibleIfConfig = getExt(MyAccountExtEnum.VISIBLE_IF, ext) as AddressExtType;
          if (visibleIfConfig) {
            const { targetFieldName, targetFieldValue } = visibleIfConfig;
            const dependencyValue = addr?.[targetFieldName as keyof Contact] ?? null;

            // This is support for fixed data that defined in options in masterdata
            const dependencyInput = inputs?.find((input: MyAccountInput) => input.fieldName === targetFieldName);
            const option = parseArray(dependencyInput?.options);

            const optionValue = option?.find((item: DynamicObjectInterface) => item.value === dependencyValue);

            // add to state
            pairs.push({
              primaryField: targetFieldName,
              secondaryField: fieldName,
              copyData: true,
            } as AddressPairFieldType);

            // Modify field value: if matched targetValue or not found in optionlist
            // Set other field value as target field value
            if ((dependencyValue === targetFieldValue || !optionValue) && dependencyValue !== null) {
              const fieldValue = iff(dependencyValue === targetFieldValue, '', dependencyValue);
              const isMatchedValue = dependencyValue === targetFieldValue;
              const targetFinalValue = iff(dependencyValue === '', '', targetFieldValue);
              processAddr = {
                ...processAddr,
                [targetFieldName]: targetFinalValue, // set refField to default value
                ...(!isMatchedValue ? { [fieldName]: fieldValue } : {}), // Do not update the field if it's same value, let it as user's input
              };
            }
          }

          // Checkbox and disable field scenario
          // Check from secondary field to primary field (No Number Checkbox to Number)
          const disableIfConfig = getExt(MyAccountExtEnum.DISABLE_FIELD_ON_CHECK, ext) as AddressExtType;
          const validDisableConfigCheck = addr?.sourceApplication === '';
          if (disableIfConfig && validDisableConfigCheck) {
            const { targetFieldName } = disableIfConfig;

            const targets = Array.isArray(targetFieldName) ? targetFieldName : [targetFieldName];
            const values: DynamicObjectInterface[] = [];

            targets.forEach((target: string) => {
              const value = addr?.[target as keyof Contact] ?? null;

              pairs.push({
                primaryField: target,
                secondaryField: fieldName,
              } as AddressPairFieldType);

              values.push(value);
            });

            const allDependencyValuesTrue = values.every((v: DynamicObjectInterface) => !!v);

            const defaultCheckValue = processAddr[fieldName as keyof Contact] ?? false;
            const isNotChecked = processAddr.contactId ? allDependencyValuesTrue : !defaultCheckValue;

            processAddr = {
              ...processAddr,
              [fieldName]: !isNotChecked,
            };
          }
        });

      inputs
        ?.filter((input: MyAccountInput) => input?.defaultValue !== undefined)
        ?.forEach(({ fieldName, defaultValue }: MyAccountInput) => {
          if (defaultValue !== undefined) {
            const fieldValue = processAddr[fieldName as keyof Contact];
            if (fieldValue === undefined || fieldValue === null) {
              processAddr = {
                ...processAddr,
                [fieldName]: defaultValue,
              };
            }
          }
        });
      setFieldPairs(pairs);
      return processAddr;
    };

    const getAddressSearchData = async (searchKey: string, state?: string, district?: string, subDistrict?: string) => {
      try {
        const removeDash = (str: string) => (str.length === 1 ? str?.replace('-', '') : str);
        setIsGuidedDataLoading(true);
        const data = await apiErrorHandler(() =>
          profileApi.getAddressRegionSearch(searchKey, {
            state: state,
            ...(district && { district: removeDash(district) }),
            ...(subDistrict && { subDistrict: removeDash(subDistrict) }),
            language: shortLocaleCode?.toLowerCase(),
          }),
        );
        setIsGuidedDataLoading(false);
        return parseArray(data);
      } catch {
        return [];
      }
    };
    const onDrawerClose = (fieldName?: string) => {
      setIsDrawerOpen(false);
      setSearchField({
        fieldName: '',
        config: {
          label: '',
          key: '',
        },
        data: [],
        selectedValue: '',
      });
      setSearchDrawerTitle('');
      if (fieldName) {
        customHandlerBlur({
          target: {
            name: parseString(fieldName),
          },
        } as React.FocusEvent<HTMLInputElement>);
      }
    };
    // <-- end of Guided input functions

    // Shipping address functions -->
    const submitShippingForm = async (values: CreateContact) => {
      // This funciton will only handle for contactType = Shipping.
      // This form will show submit button if it's for Shipping.
      // Other than shipping address, it will be handle by parent component

      if (contactType === ContactType.Shipping && canSubmit && !externalUse && !isSaving) {
        setIsSaving(true);
        const formattedPhone = parsePhoneNumber(dialCode, phoneNum);
        const cleanFields = getAddress(values as Contact);
        const payload: DynamicObjectInterface = {
          ...cleanFields,
          phone: formattedPhone,
          ownerType: ownerType,
          ownerId: ownerId, // Send profile for shipping address (OwnerType=PROFILE)
        };

        // If additional validation, then execute it first, otherwise proceed for API
        if (validationCB) {
          const errorMessage = validationCB(payload as AddressContactType);
          if (errorMessage) {
            addToast({
              id: ToastMessageEnum.SHIPPING_ADDRESS_VALIDATION_FAILED,
              timeout: 3000,
              content: <Notification hasNotificationIcon={true} theme={Theme.ERROR} message={errorMessage} />,
            });
            return;
          }
        }

        await submitShippingAddress({
          payload: payload as AddressContactType,
          originalAddress: address as AddressContactType,
          ownerProfileId: ownerId,
          isEdit,
          callback,
          successMessage: () => toastMessage(Theme.SUCCESS, successMessage),
          failedMessage: () => toastMessage(Theme.ERROR, failedMessage),
        });

        setIsSaving(false);
      }
    };
    const shippingAddrTouchAllFields = () => {
      // Set touched state for all fields
      const allTouchFields: Record<string, boolean> = {};
      allFields.forEach((field) => {
        allTouchFields[field] = true;
      });
      formik.setTouched(allTouchFields);
    };
    // <-- end of Shipping address functions

    // Validations -->
    const shippingSubmitValidation = async (isFromSubmit = false, e?: React.FormEvent | null) => {
      // This is for shipping address

      if (contactType === ContactType.Shipping) {
        try {
          await validationSchema.validate(formik.values);

          // additional validataion on phone format
          const isPhoneValid = validatePhone(dialCode, phoneNum);
          if (!isPhoneValid) {
            throw new Error('InvalidPhone');
          }

          return true;
        } catch (error) {
          logger.error('Error in shippingSubmitValidation', error);
          if (isFromSubmit) {
            e?.preventDefault();
            e?.stopPropagation();
            if (error !== 'InvalidPhone') {
              // Trigger hidden button to touch all fields
              refHiddenButton.current?.click();
            }
          }
          return false;
        }
      }
      return false;
    };
    const processPhoneValidationMessage = (
      v_dialCode?: string,
      v_phoneNumber?: string,
      required?: boolean,
      meta?: DynamicObjectInterface,
      customLabel?: string,
    ) => {
      const errorLabel = parseString(customLabel, 'profile.AddressInput.phone');
      const isPhoneValid = validatePhone(v_dialCode, v_phoneNumber);
      let phoneValidationError = '';
      const isTouched = meta?.touched || validationOnMount;

      if (isTouched) {
        // Check phone is filled and market custom format (from meta/formik)
        if (required && (!v_dialCode || !v_phoneNumber)) {
          phoneValidationError = `${errorLabel}.required`;
        }

        if (!phoneValidationError && v_dialCode && v_phoneNumber && !isPhoneValid) {
          phoneValidationError = `${errorLabel}.format`;
        }

        // if error from schema, change it to phone required and override dialcode message
        if (!phoneValidationError && v_dialCode && v_phoneNumber && meta?.error) {
          phoneValidationError = meta?.error?.replace('profile.validationMsg.phone', errorLabel);
        }
      }

      return phoneValidationError;
    };
    const updateCanSubmit = async () => {
      try {
        await validationSchema.validate(formik.values);
        setCanSubmit(formik.isValid && Object.keys(formik.touched).length > 0);
        formik.setErrors({});
      } catch {
        // No log required, just set canSubmit to false
        setCanSubmit(false);
      }
    };
    // <-- end of Validations

    // Render functions -->
    const renderTextInput = (fieldName: string, otherData?: DynamicObjectInterface) => {
      const {
        label,
        isEditableOnce,
        showCharCount,
        showAsInput,
        editable,
        required,
        max,
        min,
        excludeChars,
        ext,
        isReadOnly,
      } = otherData;
      const labelParams = { max, min };

      const handleFieldBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        customHandlerBlur(e);
        if (showCharCount) {
          setFocusedField('');
        }
      };

      const isDisableField = fieldName === ADDRESS_FIELDS.DELIVERY_INSTRUCTIONS && disableDeliveryInstructions;

      // This is use for number & no-number scenario
      const disableIfConfig = getExt(MyAccountExtEnum.DISABLE_IF, ext) as AddressExtType;
      const { targetFieldName, targetFieldValue } = disableIfConfig || {};
      const dependencyValue = formik.values?.[targetFieldName] ?? null;
      const disableByFieldFlag = dependencyValue === targetFieldValue;

      // This is to check required if scenario
      const requireIfConfig = getExt(MyAccountExtEnum.REQUIRE_IF, ext) as AddressExtType;
      const reqValue = formik.values?.[requireIfConfig?.targetFieldName] ?? null;
      const finalRequiredFlag = requireIfConfig ? reqValue === requireIfConfig?.targetFieldValue : required;

      // Reset this field when it's disabled by other field
      const value = disableByFieldFlag ? '' : formik.values?.[fieldName];

      const labelTxt = processLabelText(t, label, finalRequiredFlag);

      if (editable) {
        const meta = formik.getFieldMeta(fieldName);
        const isDisabled = disabled || isDisableField || disableByFieldFlag;

        const isTouched = !isDisabled && isFieldTouched(validationOnMount, meta.touched, isEditableOnce);
        const errMsg = meta.error;

        return (
          <>
            <Input
              value={value}
              label={labelTxt}
              name={`${contactType}-${fieldName}`}
              onChange={onTextChangeHandler}
              onKeyUp={onTextKeyUpHandler}
              maxLength={max}
              // required={finalRequiredFlag} // Temporary comment this due to framework will render *
              disabled={isDisabled || isReadOnly}
              onBlur={handleFieldBlur}
              status={inputErrorStatus(isTouched, meta.error)}
              data-testid={`address-input-${fieldName}-testid`}
              message={inputErrorMessage(t, isTouched, errMsg, labelParams)}
              onFocus={() => (showCharCount ? setFocusedField(fieldName) : null)}
              autoComplete="off"
            />

            {focusedField === fieldName && showCharCount && (
              <span className={styles['character-count']}>
                {parseNumber(getCleanString(value, excludeChars)?.length)}/{max}
              </span>
            )}
          </>
        );
      } else {
        return <DisplayField fieldName={fieldName} label={labelTxt} value={value} showAsInput={showAsInput} />;
      }
    };

    const renderCheckboxInput = (fieldName: string, otherData?: DynamicObjectInterface) => {
      const { label, editable } = otherData;
      const value = formik.values?.[fieldName];

      const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue(fieldName, e.target.checked);

        if (e.target.checked) {
          // Empty dependency fields
          fieldPairs
            ?.filter((pair: DynamicObjectInterface) => pair.secondaryField === fieldName)
            ?.forEach((pair: AddressPairFieldType) => {
              const primaryField = parseString(pair.primaryField);
              if (primaryField) {
                formik.setFieldValue(primaryField, '', false);
              }
            });
        }
      };

      if (editable) {
        return (
          <div className={styles['checkbox-input-container']}>
            <Checkbox
              name={`${contactType}-${fieldName}`}
              id={`${contactType}-${fieldName}`}
              text={label}
              checked={value}
              onChange={handleCheckboxChange}
              className={styles['checkbox-input']}
              disabled={disabled}
            />
          </div>
        );
      } else {
        return <DisplayField fieldName={fieldName} label={label} value={value} />;
      }
    };

    const renderPhoneInput = (
      fieldName: string,
      v_dialCode: string,
      v_phoneNumber: string,
      otherData?: DynamicObjectInterface,
    ) => {
      const { label, customLabel, editable, required, disabledCountry, isReadOnly } = otherData;

      if (!editable) {
        return <></>;
      }

      let countryLabelTxt = t('profile.AddressInput.country');

      if (disabledCountry) {
        countryLabelTxt = '';
      }

      const meta = formik.getFieldMeta(fieldName);

      const phoneErrorMessage = processPhoneValidationMessage(v_dialCode, v_phoneNumber, required, meta, customLabel);
      const phoneValidationStatus = phoneErrorMessage ? InputValidations.INVALID : InputValidations.DEFAULT;

      const labelTxt = processLabelText(t, t(label), required);

      return (
        <div>
          <PhoneNumberInput
            name={fieldName}
            dropdownLabel={countryLabelTxt}
            inputLabel={labelTxt}
            configData={{
              label: 'name',
              onSelect: (e: DynamicObjectInterface) => {
                setDialCode(e.name);
                formik.setFieldValue(fieldName, parsePhoneNumber(e.name, v_phoneNumber));
              },
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              customHandlerBlur(e);
              handlePhoneChange(formik, fieldName, v_dialCode, e.target?.value, required);

              if (phoneErrorMessage && contactType === ContactType.Shipping) {
                sendUtagView(ProfileEventsEnum.MyAccountShippingMobileError, dataLayer, {
                  phoneNumber: e.target?.value,
                  dialCode: v_dialCode,
                  errorMessage: phoneErrorMessage,
                });
              }
            }}
            onBlurCapture={customHandlerBlur}
            inputValue={v_phoneNumber}
            dropdownDisabled={disabledCountry || disabled}
            disabled={disabled || isReadOnly}
            onInputChange={(number?: string) => {
              const num = parseString(number);
              setPhoneNum(num);
              handlePhoneChange(formik, fieldName, v_dialCode, num, required);
            }}
            dropdownValue={v_dialCode}
            disabledFilter
            autoComplete="off"
            validationStatus={phoneValidationStatus}
            validationMessage={phoneValidationStatus && phoneErrorMessage && t(phoneErrorMessage)}
          />
        </div>
      );
    };

    const getRequiredDependencyField = (fieldName: string) => {
      const depField = inputs.find((input: MyAccountInput) => input.fieldName === fieldName);

      if (depField?.validations?.required || !guidedInputSkipOptionalField) {
        return fieldName;
      } else {
        const dependencyField = parseString(getExt(MyAccountExtEnum.DEPENDENCY, depField?.ext) as string);
        return dependencyField;
      }
    };
    const handleGuidedOnClick = async (
      fieldName: string,
      label: string,
      selectedValue?: string,
      searchKey?: string,
      options?: MyAccountOption,
    ) => {
      setSearchDrawerTitle(label);
      const { data } = await guidedInputConfig(fieldName, searchKey, options);

      setSearchField({
        fieldName,
        data,
        selectedValue: selectedValue,
        config: {
          label: 'name',
          key: 'value',
        },
      });
    };

    const renderGuidedInput = (fieldName: string, otherData?: DynamicObjectInterface) => {
      const { label, editable, required, isEditableOnce, ext, customLabel, options, isReadOnly } = otherData;

      const dependencyField = parseString(getExt(MyAccountExtEnum.DEPENDENCY, ext) as string);
      const dependencyFieldRequired = getRequiredDependencyField(dependencyField);
      const dependencyValue = formik.values?.[dependencyFieldRequired] ?? null;
      const searchKey = parseString(getExt(MyAccountExtEnum.SEARCH_KEY, ext) as string);
      const isDisabled = (!!dependencyFieldRequired && !dependencyValue) || disabled || !isGuidedDataReady;
      const labelTxt = processLabelText(t, label, required);
      let dispValue = parseString(formik.values?.[fieldName]);
      const customFieldname = parseString(customLabel?.split('.').pop());

      const selectedValue = formik.values?.[fieldName];

      if (fieldName === ADDRESS_FIELDS.STATE) {
        dispValue = parseString(regionData.find((item: DynamicObjectInterface) => item.value === selectedValue)?.name);
      } else if (options) {
        const optionFallbackValue = getExt(MyAccountExtEnum.OPTION_FALLBACK_VALUE, ext) as string;
        const optionData = getGuidedOptions(fieldName);
        const { data } = optionData || {};

        const optionValue = parseArray(data).find((item: DynamicObjectInterface) => item.value === selectedValue)?.name;
        dispValue = parseString(optionValue);

        if (optionFallbackValue && !dispValue && address?.contactId && selectedValue) {
          dispValue = parseArray(data).find((item: DynamicObjectInterface) => item.value === optionFallbackValue)?.name;
        }
      }

      if (!editable) {
        return <></>;
      }

      const meta = formik.getFieldMeta(fieldName);
      const isTouched = !isDisabled && isFieldTouched(validationOnMount, meta.touched, isEditableOnce);

      let errMsg = meta.error;
      if (required) {
        const errorField = parseString(customFieldname, fieldName);
        errMsg = conditionalError(meta.error, `profile.validationMsg.${errorField}.required`);
      }

      const processData = () => handleGuidedOnClick(fieldName, labelTxt, selectedValue, searchKey, options);

      const openDrawer = () => {
        startSearchDrawer(setIsDrawerOpen, processData).then(({ fieldName: field, item }) => {
          handleGuidedInputChanged(field, item, !!options);
          logger.log('Guided input changed - onMouseUp', field, item);
          setTimeout(() => {
            document?.getElementById?.('shipping-address-form-id')?.click();
            logger.log('Guided input changed - Click Somewhere');
          }, 500);
        });
      };

      return (
        <DropDown
          value={dispValue}
          dropdownLabel={labelTxt}
          onClick={openDrawer}
          name={`${contactType}-${fieldName}`}
          id={fieldName}
          disabled={isDisabled || !isGuidedDataReady || isReadOnly}
          required={required}
          disabledFilter
          className={styles['guided-input']}
          data-testid={`address-input-${fieldName}-testid`}
          status={inputErrorStatus(isTouched, errMsg)}
          message={inputErrorMessage(t, isTouched, errMsg)}
        />
      );
    };

    const renderInGroup = (halfWidth: boolean, renderHalfWidthInputs: JSX.Element[], renderInput: JSX.Element) => {
      if (halfWidth) {
        renderHalfWidthInputs.push(renderInput);

        if (renderHalfWidthInputs.length === 2) {
          return (
            <div className={styles['grouped-fields']}>
              {renderHalfWidthInputs.slice(renderHalfWidthInputs.length - 2, renderHalfWidthInputs.length)}
            </div>
          );
        } else {
          return <></>;
        }
      } else {
        return renderInput;
      }
    };

    const processRenderDependencyFields = (ext?: MyAccountOption[], contact?: Contact) => {
      // To handle show other input field
      const visibleIfConfig = getExt(MyAccountExtEnum.VISIBLE_IF, ext) as AddressExtType;
      const { targetFieldName, targetFieldValue, valueNotInOptions } = visibleIfConfig || {};

      const dependencyData = [];
      if (valueNotInOptions) {
        const guidedData = getGuidedOptions(targetFieldName);
        const { data } = guidedData || {};
        dependencyData.push(...parseArray(data));
      }

      const dependencyValue = formik.values?.[targetFieldName] ?? null;

      if (
        targetFieldName &&
        targetFieldValue &&
        dependencyValue !== targetFieldValue &&
        ((dependencyData.find((item: DynamicObjectInterface) => item?.value === dependencyValue) &&
          contact?.contactId) ||
          !contact?.contactId ||
          !dependencyValue)
      ) {
        return true; // hide
      }

      return false; // show
    };

    const checkFieldHideCondition = (isHideIfConfirmation?: boolean, hideConfirmButton?: boolean) =>
      (isHideIfConfirmation && !hideConfirmButton) || !isHideIfConfirmation;

    const renderFields = (inputs: MyAccountInput[]) => {
      const renderAddressInputs: JSX.Element[] = [];
      const renderShippingInputs: JSX.Element[] = [];

      // This is to render 2 fiels in 1 row
      const renderHalfWidthInputs: JSX.Element[] = [];

      inputs.forEach((input: MyAccountInput) => {
        const { fieldName, validations = {}, editable, isEditableOnce, ext, visible, options } = input;
        const { required, max = '', min = '' } = validations;

        const halfWidth = getExt(MyAccountExtEnum.UI_HALF_WIDTH, ext) as boolean;
        const showAsInput = getExt(MyAccountExtEnum.DISPLAY_AS_INPUT, ext) as boolean;
        const defaultCountryFlag = getExt(MyAccountExtEnum.DEFAULT_COUNTRY_VALUE, ext) as boolean;

        const disabledCountry = getExt(MyAccountExtEnum.COUNTRY_NOT_EDITABLE, ext) as boolean;
        const isShippingField = (getExt(MyAccountExtEnum.TYPE, ext) as string) === ADDRESS_FIELDS.SHIPPING_DETAILS;
        const isHideIfConfirmation = getExt(MyAccountExtEnum.HIDE_IF_CONFIRMATION, ext) as boolean;
        const showCharCount = charCountFields.includes(fieldName);

        const fieldType = addressFieldTypeByInput(input);
        const customLabel = getExt(MyAccountExtEnum.CUSTOM_LABEL_KEY, ext) as string;

        const label = visible
          ? addressFieldLabel(t, fieldName, contactType as ContactType, isShippingField, customLabel)
          : '';

        const excludeChars = getExt(MyAccountExtEnum.EXCLUDE_CHARS, ext) as string;

        // Custom string limit by env
        const customStrLimits = getExt(MyAccountExtEnum.STRING_LIMIT_BY_ENV, ext) as
          | { max?: number; min?: number }
          | undefined;
        const newMax = customStrLimits && typeof customStrLimits.max !== 'undefined' ? customStrLimits.max : max;
        const newMin = customStrLimits && typeof customStrLimits.min !== 'undefined' ? customStrLimits.min : min;

        // To handle show other input field
        const skipThisField = processRenderDependencyFields(ext, address as Contact);
        if (skipThisField) {
          return;
        }

        if (defaultCountryFlag) {
          setDialCode(defaultDialCode);
        }

        // Check is readonly fields
        let isReadOnly = false;
        if (readonlyFields[fieldName]) {
          isReadOnly = true;
        }

        if (checkFieldHideCondition(isHideIfConfirmation, hideConfirmButton)) {
          let renderInput: JSX.Element = <></>;

          switch (fieldType) {
            case MyAccountFieldType.TEXT: {
              // Text input
              renderInput = (
                <div key={fieldName}>
                  {renderTextInput(fieldName, {
                    label,
                    isEditableOnce,
                    showCharCount,
                    showAsInput,
                    editable,
                    required,
                    max: newMax,
                    min: newMin,
                    excludeChars,
                    ext,
                    isReadOnly,
                  })}
                </div>
              );

              break;
            }

            case MyAccountFieldType.GUIDED_INPUT: {
              // Dropdown input
              renderInput = (
                <div key={fieldName}>
                  {renderGuidedInput(
                    fieldName,

                    {
                      label,
                      customLabel,
                      editable,
                      required,
                      isEditableOnce,
                      ext,
                      options,
                      isReadOnly,
                    },
                  )}
                </div>
              );
              break;
            }

            case MyAccountFieldType.PHONE: {
              renderInput = (
                <div key={fieldName}>
                  {renderPhoneInput(fieldName, dialCode, phoneNum, {
                    label,
                    customLabel,
                    showAsInput,
                    editable,
                    required,
                    disabledCountry,
                    isReadOnly,
                  })}
                </div>
              );
              break;
            }

            case MyAccountFieldType.CHECKBOX: {
              // Text input
              renderInput = (
                <div key={fieldName}>
                  {renderCheckboxInput(fieldName, {
                    label,
                    isEditableOnce,
                    showCharCount,
                    showAsInput,
                    editable,
                    required,
                    max,
                    min,
                    excludeChars,
                    ext,
                    isReadOnly,
                  })}
                </div>
              );

              break;
            }

            default:
          }

          renderInput = renderInGroup(halfWidth, renderHalfWidthInputs, renderInput);

          if (isShippingField) {
            renderShippingInputs.push(renderInput);
          } else {
            renderAddressInputs.push(renderInput);
          }
        }
      });
      return { renderAddressInputs, renderShippingInputs };
    };
    // <-- end of Render functions

    // Handlers -->
    const customHandlerBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // the field name format 'SHIPPING-<fieldName>',
      // so we need to extract the field name (to handle 2 address input in a screen)
      const { name } = e.target;
      const [, ...rest] = String(name).split('-');
      const fieldName = parseString(rest.join('-'), name);
      formik.setFieldTouched(fieldName);
    };
    const onTextChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      const [, ...rest] = String(name).split('-');
      const fieldName = parseString(rest.join('-'), name);
      const trimmedValue = sanitizeInput(value);
      formik.setFieldValue(fieldName, trimmedValue, true);
    };

    const onTextKeyUpHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { name } = e.currentTarget;
      const [, ...rest] = String(name).split('-');
      const fieldName = parseString(rest.join('-'), name);

      formik.setFieldTouched(fieldName, true, true);
    };

    const handleGuidedInputChanged = async (fieldName: string, item: DynamicObjectInterface, useValue = false) => {
      const { value, name } = item;

      let valueToUpdate = name;
      if (fieldName === ADDRESS_FIELDS.STATE || useValue) {
        valueToUpdate = value;
      }

      await formik.setFieldValue(fieldName, valueToUpdate);
      await processDependencyFields(inputs, fieldName, formik, readonlyFields);
      setIsAddressChanged(true);

      const secondaryField = parseString(
        fieldPairs?.find((pair: DynamicObjectInterface) => pair.primaryField === fieldName)?.secondaryField,
      );
      if (secondaryField) {
        formik.setFieldValue(secondaryField, '');
        // Remove readonly field if secondary field is changed
        if (readonlyFields?.[secondaryField]) {
          delete readonlyFields[secondaryField];
        }
      }
    };
    // <-- end of Handlers

    const { renderAddressInputs, renderShippingInputs } = useMemo(
      () => renderFields(inputs),
      [
        inputs,
        formik.values,
        formik.touched,
        formik.errors,
        dialCode,
        phoneNum,
        regionData,
        disabled,
        useMobileView,
        useDesktopView,
        isEdit,
        showDefaultCheckbox,
        hideConfirmButton,
        isLoading,
        useAvailableWidth,
      ],
    );

    return (
      <div
        className={`${
          hideConfirmButton ? styles['address-input-drawer'] : styles['address-input-container']
        } ${className} `}
        data-testid="address-input-container-testid"
        id="address-input-container-id"
      >
        {header && (
          <div className={`${styles['address-custom-header']} ${iff(useMobileView, styles['mobile-view'], '')}`}>
            {header}
          </div>
        )}
        {addressTypeLabel && (
          <div ref={scrollRef} className={styles['address-type-title']} data-testid="address-title-testid">
            {addressTypeLabel}
          </div>
        )}
        {isLookupEnabled && !showAddressForm && (
          <div>
            <AddressLookup
              callback={addressLookupCallback}
              allowEnterManually={allowEnterManually}
              contactType={contactType as ContactType}
              status={status}
              title={title}
            />
            {message && status === InputValidations.INVALID && (
              <InputErrorComponent t={t} touched={true} error={message} />
            )}
            {message && status !== InputValidations.INVALID && message}
          </div>
        )}
        {(!isLookupEnabled || showAddressForm) && (
          <div className={styles['address-form']}>
            <form
              id="shipping-address-form-id"
              data-testid="shipping-address-form-testid"
              onSubmitCapture={(e: React.FormEvent) => {
                e.preventDefault();
                if (canSubmit) {
                  shippingSubmitValidation(true, e);
                }
              }}
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit) {
                  formik.handleSubmit(e);
                }
              }}
            >
              <div className={`${styles['general-address-container']} ${mobileStyle} ${desktopStyle}`}>
                {renderAddressInputs}
              </div>

              {showShippingParticulars && renderShippingInputs.length > 0 && (
                <div className={`${styles['shipping-detail-container']} ${mobileStyle} ${desktopStyle}`}>
                  <div>
                    {renderShippingInputs}

                    {showDefaultCheckbox && (
                      <Checkbox
                        data-testid="default-checkbox-testid"
                        name={`${contactType}-isDefault`}
                        id={`${contactType}-isDefault`}
                        text={t('profile.AddressInput.defaultAddressCheckboxLabel')}
                        className="default-address-checkbox"
                        checked={formik.values?.isDefault}
                        disabled={isDefaultAddress}
                        onChange={() => {
                          formik.setFieldValue('isDefault', !formik.values?.isDefault);
                        }}
                      />
                    )}
                    {!hideConfirmButton && !externalUse && (
                      <div className={styles['shipping-address-form-btn']}>
                        <ButtonWithLoading
                          title={t('profile.AddressInput.confirmButton')}
                          type={ButtonTypes.SUBMIT}
                          data-testid="confirm-button-testid"
                          buttonStyle={ButtonStyles.SOLID}
                          size={ButtonSize.MEDIUM}
                          disabled={!canSubmit}
                          isLoading={isLoading}
                        />
                      </div>
                    )}
                    {contactType === ContactType.Shipping && hideConfirmButton && !externalUse && (
                      <div className={styles['hidden-button']}>
                        <Button
                          onClick={() => shippingAddrTouchAllFields()}
                          ref={refHiddenButton}
                          data-testid="hidden-button"
                        >
                          {' '}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </form>
          </div>
        )}
        {isDrawerOpen && (
          <SearchDrawer
            {...searchField}
            callback={(window as DynamicObjectInterface).handleSearchDrawerSelect}
            onClose={onDrawerClose}
            isLoading={isGuidedDataLoading}
            title={searchDrawerTitle}
          />
        )}
      </div>
    );
  },
);
AddressInput.displayName = 'AddressInput';

export default AddressInput;
